import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, Inbox, Quote, Sparkles, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/admin/Field";

function StatCard({ label, value, sublabel, accent }) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 transition hover:border-white/15">
      <div className="absolute right-4 top-4 h-2 w-2 rounded-full bg-[#0055ff] opacity-0 transition group-hover:opacity-100" />
      <div className="font-body text-[10px] tracking-[0.3em] uppercase text-white/40">{label}</div>
      <div className="mt-3 flex items-baseline gap-3">
        <div className="font-display text-4xl font-medium tracking-tight text-white">{value ?? "—"}</div>
        {sublabel && <div className="font-body text-xs text-white/45">{sublabel}</div>}
      </div>
      {accent && <div className="mt-4 h-px w-12 bg-[#0055ff]" />}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/messages")])
      .then(([s, m]) => {
        setStats(s.data);
        setMessages(m.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-col gap-2">
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Overview</span>
        <h1 className="font-display text-3xl font-medium tracking-tight text-white md:text-4xl">Studio control room</h1>
        <p className="font-body text-sm text-white/50">Everything that powers the public site, in one place.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)
        ) : (
          <>
            <StatCard
              label="Projects"
              value={stats?.projects ?? 0}
              sublabel={`${stats?.projects_published ?? 0} published`}
              accent
            />
            <StatCard label="Featured" value={stats?.projects_featured ?? 0} sublabel="on homepage" />
            <StatCard label="Services" value={stats?.services ?? 0} />
            <StatCard
              label="Messages"
              value={stats?.messages ?? 0}
              sublabel={`${stats?.messages_unread ?? 0} unread`}
            />
          </>
        )}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 md:p-8">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-medium tracking-tight text-white">Latest enquiries</h2>
              <p className="font-body text-xs text-white/45">Most recent contact-form messages.</p>
            </div>
            <Link
              to="/admin/messages"
              className="inline-flex items-center gap-1 font-body text-xs text-white/55 hover:text-white"
            >
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : messages.length === 0 ? (
            <div className="py-10 text-center font-body text-sm text-white/45">
              No messages yet. Once visitors submit the contact form, they’ll show up here.
            </div>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {messages.slice(0, 5).map((m) => (
                <li key={m.id} className="flex items-start justify-between gap-4 py-3.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm text-white">{m.name}</span>
                      {!m.is_read && (
                        <span className="rounded-full bg-[#0055ff]/15 px-2 py-0.5 font-body text-[10px] uppercase tracking-wider text-[#7aa6ff]">
                          New
                        </span>
                      )}
                    </div>
                    <div className="truncate font-body text-xs text-white/45">{m.email} · {m.project_type || "general"}</div>
                    <p className="mt-1 line-clamp-1 font-body text-sm text-white/65">{m.message}</p>
                  </div>
                  <div className="shrink-0 font-mono text-[10px] text-white/35">
                    {new Date(m.created_at).toLocaleDateString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 md:p-8">
          <h2 className="font-display text-lg font-medium tracking-tight text-white">Quick actions</h2>
          <div className="mt-4 grid gap-3">
            <QuickLink to="/admin/portfolio/new" icon={FolderKanban} label="Add a project" />
            <QuickLink to="/admin/services" icon={Sparkles} label="Manage services" />
            <QuickLink to="/admin/testimonials" icon={Quote} label="Manage testimonials" />
            <QuickLink to="/admin/messages" icon={Inbox} label="Read messages" />
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 font-body text-sm text-white/75 transition hover:border-[#0055ff]/40 hover:bg-white/[0.04] hover:text-white"
    >
      <span className="flex items-center gap-3">
        <Icon className="h-4 w-4" />
        {label}
      </span>
      <ArrowUpRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />
    </Link>
  );
}
